<script lang="ts">
    import { connection } from "../lib/connection.svelte.ts";
    import { commandInput } from "../lib/commandInput.svelte.ts";

    let inputEl: HTMLInputElement;

    const HISTORY_KEY = "arkham.commandHistory";
    const HISTORY_MAX = 500;

    function loadHistory(): string[] {
        try {
            const raw = localStorage.getItem(HISTORY_KEY);
            return raw ? JSON.parse(raw) : [];
        } catch { return []; }
    }

    function saveHistory(h: string[]) {
        try { localStorage.setItem(HISTORY_KEY, JSON.stringify(h)); } catch {}
    }

    const history: string[] = loadHistory();
    let historyPos = -1;
    let savedInput = "";

    // Tab-completion state — local to InputBar, not shared.
    let completionBase = "";  // value before the first Tab
    let completionIdx  = -1;

    function sendLines(text: string) {
        const lines = text.split(/\r\n|\r|\n/).map(l => l.trimEnd());
		// Trim off empty trailing lines
        while (lines.length > 0 && lines[lines.length-1] === '') lines.pop();
        for (const line of lines) connection.send(line);
    }

    function pushHistory(text: string) {
        history.push(text);
        if (history.length > HISTORY_MAX) history.splice(0, history.length - HISTORY_MAX);
        saveHistory(history);
        historyPos = -1;
        savedInput = "";
        commandInput.completions = [];
        completionIdx = -1;
        completionBase = "";
    }

    function submit(e: SubmitEvent) {
        e.preventDefault();
        const text = commandInput.value.trim();
        if (!text) return;
        pushHistory(text);
        sendLines(text);
        commandInput.value = "";
    }

    function handlePaste(e: ClipboardEvent) {
        const text = e.clipboardData?.getData('text') ?? '';
        if (!/[\r\n]/.test(text)) return; // single-line: let browser handle it
        e.preventDefault();
        const trimmed = text.trimEnd();
        if (!trimmed) return;
        pushHistory(trimmed);
        sendLines(trimmed);
        commandInput.value = "";
    }

    function handleTab() {
        const comps = commandInput.completions;
        if (comps.length > 0) {
            // Cycle through existing completions, replacing the trailing fragment.
            completionIdx = (completionIdx + 1) % comps.length;
            const partial = completionBase.match(/\S+$/)?.[0] ?? "";
            const prefix  = completionBase.slice(0, completionBase.length - partial.length);
            commandInput.value = prefix + comps[completionIdx];
        } else {
            // First Tab: ask the server for completions.
            const partial = commandInput.value.match(/\S+$/)?.[0];
            if (partial) {
                completionBase = commandInput.value;
                completionIdx  = -1;
                connection.requestCompletion(partial);
            }
        }
    }

    function onKeydown(e: KeyboardEvent) {
        if (e.key === "Tab") {
            e.preventDefault();
            handleTab();
            return;
        }

        // Any non-navigation key resets completion state.
        if (e.key !== "ArrowUp" && e.key !== "ArrowDown") {
            commandInput.completions = [];
            completionIdx  = -1;
            completionBase = "";
        }

        if (e.key === "ArrowUp") {
            e.preventDefault();
            if (history.length === 0) return;
            if (historyPos === -1) {
                savedInput = commandInput.value;
                historyPos = history.length - 1;
            } else if (historyPos > 0) {
                historyPos--;
            }
            commandInput.value = history[historyPos];
        } else if (e.key === "ArrowDown") {
            e.preventDefault();
            if (historyPos === -1) return;
            if (historyPos < history.length - 1) {
                historyPos++;
                commandInput.value = history[historyPos];
            } else {
                historyPos = -1;
                commandInput.value = savedInput;
            }
        }
    }

    // Focus and move cursor to end whenever something prefills the input externally.
    $effect(() => {
        const _ = commandInput.focusRevision;
        inputEl?.focus();
        // Defer so the input's value has settled before we move the cursor.
        requestAnimationFrame(() => {
            const len = commandInput.value.length;
            inputEl?.setSelectionRange(len, len);
        });
    });

    export function focus() {
        inputEl?.focus();
    }
</script>

<!-- svelte-ignore a11y_autofocus -->
<form class="input-bar" onsubmit={submit}>
    <span class="prompt">&gt;</span>
    <input
        bind:this={inputEl}
        bind:value={commandInput.value}
        onkeydown={onKeydown}
        onpaste={handlePaste}
        type="text"
        autocomplete="off"
        autocorrect="off"
        autocapitalize="off"
        spellcheck="false"
        placeholder="enter command"
        autofocus
    />
    {#if commandInput.completions.length > 0}
        <span class="completion-hint">
            {commandInput.completions.length} match{commandInput.completions.length === 1 ? "" : "es"} · Tab to cycle
        </span>
    {/if}
</form>

<style>
    .input-bar {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        background: rgba(5, 4, 3, 0.6);
        padding: 0.45rem 0.6rem;
        flex-shrink: 0;
    }

    .prompt {
        font-family: 'Courier New', Courier, monospace;
        color: #6a6050;
        font-size: 0.9rem;
        user-select: none;
        flex-shrink: 0;
    }

    input {
        flex: 1;
        background: transparent;
        border: none;
        outline: none;
        color: #d4c9a8;
        font-family: 'Courier New', Courier, monospace;
        font-size: 0.9rem;
        caret-color: #c5a860;
    }

    input::placeholder {
        color: #44403a;
    }

    .completion-hint {
        flex-shrink: 0;
        font-family: 'Courier New', Courier, monospace;
        font-size: 0.7rem;
        color: #5a5040;
        white-space: nowrap;
    }
</style>
